import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Plus,
  Trash2,
  Loader2,
  MoreVertical,
  Share2,
  Gift,
  Pencil,
  Check,
  X,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getWishlistById,
  getWishItems,
  deleteWishItem,
  deleteWishlist,
  updateWishlist,
  updateWishItem,
  type WishItem,
  type Wishlist,
} from "@/services/wishlists";
import AddWishModal from "@/components/wishlist/AddWishModal";
import WishlistItemCard from "@/components/wishlist/WishlistItemCard";

const WishlistDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const [wishlist, setWishlist] = useState<Wishlist | null>(null);
  const [items, setItems] = useState<WishItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const titleInputRef = useRef<HTMLInputElement>(null);

  const isOwner = user && wishlist && user.id === wishlist.user_id;

  const load = async () => {
    if (!id) return;
    setLoading(true);
    const [wlRes, itemsRes] = await Promise.all([
      getWishlistById(id),
      getWishItems(id),
    ]);
    setWishlist(wlRes.data);
    setItems(itemsRes.data || []);
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) load();
  }, [id, authLoading]);

  const handleDeleteItem = async (itemId: string) => {
    const { error } = await deleteWishItem(itemId);
    if (!error) setItems((prev) => prev.filter((i) => i.id !== itemId));
  };

  const handleDeleteList = async () => {
    if (!wishlist || !confirm("Delete this entire list and all its items?")) return;
    const { error } = await deleteWishlist(wishlist.id);
    if (error) {
      toast({ variant: "destructive", title: "Error", description: "Could not delete list." });
    } else {
      toast({ title: "Deleted", description: "List removed." });
      navigate("/wishlists");
    }
  };

  const handleClaimItem = async (itemId: string) => {
    if (!user) return;
    const displayName = user.user_metadata?.display_name || user.email || "Someone";
    const { error } = await updateWishItem(itemId, {
      is_claimed: true,
      claimed_by_name: displayName,
      claimed_at: new Date().toISOString(),
    });
    if (!error) {
      setItems((prev) =>
        prev.map((i) =>
          i.id === itemId
            ? { ...i, is_claimed: true, claimed_by_name: displayName, claimed_at: new Date().toISOString() }
            : i
        )
      );
      toast({ title: "Claimed!", description: "This wish has been marked as yours." });
    }
  };

  const handleSaveTitle = async () => {
    if (!wishlist || !titleDraft.trim()) return;
    const { error } = await updateWishlist(wishlist.id, { title: titleDraft.trim() });
    if (!error) {
      setWishlist({ ...wishlist, title: titleDraft.trim() });
    }
    setEditingTitle(false);
  };

  const startEditingTitle = () => {
    if (!wishlist) return;
    setTitleDraft(wishlist.title);
    setEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 50);
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-muted-foreground" size={24} />
      </div>
    );
  }

  if (!wishlist) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader onBack="/wishlists" userMenuVariant="dashboard" />
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <p className="text-muted-foreground">List not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Decorative background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.4]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary) / 0.07) 1px, transparent 0)`,
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative z-10">
        <AppHeader onBack={isOwner ? "/wishlists" : undefined} userMenuVariant="dashboard" useAuthSheet={!user} />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-28 sm:pb-10">
          {/* Header section — wider and more elegant on desktop */}
          <div className="flex items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div className="flex-1 min-w-0">
              {isOwner && editingTitle ? (
                <div className="flex items-center gap-2">
                  <Input
                    ref={titleInputRef}
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveTitle();
                      if (e.key === "Escape") setEditingTitle(false);
                    }}
                    className="text-xl sm:text-2xl font-bold font-serif h-10 sm:h-12 border-border max-w-md"
                  />
                  <button onClick={handleSaveTitle} className="p-2 text-primary shrink-0 hover:bg-primary/10 rounded-full transition-colors">
                    <Check size={18} />
                  </button>
                  <button onClick={() => setEditingTitle(false)} className="p-2 text-muted-foreground shrink-0 hover:bg-muted rounded-full transition-colors">
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl sm:text-3xl font-bold font-serif text-foreground truncate">
                      {wishlist.title}
                    </h1>
                    {isOwner && (
                      <button onClick={startEditingTitle} className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors shrink-0">
                        <Pencil size={16} />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] sm:text-xs font-light uppercase tracking-[0.25em] text-primary">
                      {wishlist.type}
                    </span>
                    <span className="text-[10px] sm:text-xs text-muted-foreground">
                      · {items.length} {items.length === 1 ? "wish" : "wishes"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {/* Desktop add button */}
              {isOwner && (
                <Button
                  onClick={() => setAddOpen(true)}
                  className="hidden sm:inline-flex rounded-full shadow-sm"
                  size="sm"
                >
                  <Plus size={16} className="mr-1.5" />
                  {t("wishlist.addWish", { defaultValue: "Add Wish" })}
                </Button>
              )}

              {/* Overflow menu — owner only */}
              {isOwner && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-2 rounded-full hover:bg-muted transition-colors shrink-0">
                      <MoreVertical size={18} className="text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem className="gap-2 cursor-pointer">
                      <Share2 size={14} />
                      Share List
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDeleteList}
                      className="text-destructive gap-2 cursor-pointer"
                    >
                      <Trash2 size={14} />
                      Delete List
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Separator */}
          <div className="h-px bg-border mb-6 sm:mb-8" />

          {/* Items grid — responsive columns */}
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 sm:py-24 text-center">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-primary/10 flex items-center justify-center mb-5">
                <Gift size={28} className="text-primary sm:hidden" />
                <Gift size={36} className="text-primary hidden sm:block" />
              </div>
              <p className="font-serif text-lg sm:text-xl font-semibold text-foreground mb-1">No wishes yet</p>
              <p className="text-sm text-muted-foreground max-w-[280px]">
                {isOwner
                  ? "Add your first wish to get this list started"
                  : "This list is empty for now"}
              </p>
              {isOwner && (
                <Button
                  onClick={() => setAddOpen(true)}
                  className="mt-6 rounded-full"
                  size="lg"
                >
                  <Plus size={18} className="mr-1.5" />
                  {t("wishlist.addWish", { defaultValue: "Add Wish" })}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
              {items.map((item) => (
                <WishlistItemCard
                  key={item.id}
                  item={item}
                  isOwner={!!isOwner}
                  isAuthenticated={!!user}
                  onDelete={handleDeleteItem}
                  onClaim={handleClaimItem}
                />
              ))}
            </div>
          )}
        </div>

        {/* Sticky bottom CTA — mobile only */}
        {isOwner && (
          <div className="fixed bottom-0 left-0 right-0 z-30 bg-background/95 backdrop-blur-sm border-t border-border sm:hidden">
            <div className="max-w-lg mx-auto px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
              <Button
                onClick={() => setAddOpen(true)}
                className="w-full rounded-full shadow-lg"
                size="lg"
              >
                <Plus size={18} className="mr-1.5" />
                {t("wishlist.addWish", { defaultValue: "Add Wish" })}
              </Button>
            </div>
          </div>
        )}
      </div>

      {isOwner && (
        <AddWishModal
          open={addOpen}
          onOpenChange={setAddOpen}
          wishlistId={wishlist.id}
          onAdded={() => {
            setAddOpen(false);
            load();
          }}
        />
      )}
    </div>
  );
};

export default WishlistDetailPage;
